/**
 * Player Name Mapping Utility
 *
 * Provides functions to map BBO usernames to actual player names
 * using a persistent database of mappings.
 */

import { supabase } from '../supabaseClient';

// In-memory cache of mappings (username lowercase -> actual name)
let nameCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Load all player name mappings from the database
 * @returns {Promise<Map>} Map of lowercase username -> actual name
 */
export async function loadPlayerMappings() {
  try {
    const { data, error } = await supabase
      .from('player_name_mappings')
      .select('username, actual_name');

    if (error) {
      console.error('Error loading player name mappings:', error);
      return new Map();
    }

    // Create case-insensitive map
    const mappings = new Map();
    if (data) {
      data.forEach(mapping => {
        mappings.set(mapping.username.toLowerCase(), mapping.actual_name);
      });
    }

    // Update cache
    nameCache = mappings;
    cacheTimestamp = Date.now();

    return mappings;
  } catch (error) {
    console.error('Exception loading player name mappings:', error);
    return new Map();
  }
}

/**
 * Get cached mappings or load from database if cache is stale
 * @returns {Promise<Map>} Map of lowercase username -> actual name
 */
async function getMappings() {
  const now = Date.now();

  // If cache exists and is fresh, use it
  if (nameCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return nameCache;
  }

  // Otherwise, reload from database
  return await loadPlayerMappings();
}

/**
 * Map a single player name (username -> actual name if mapping exists)
 * @param {string} name - Player name or username
 * @returns {Promise<string>} Actual name if mapping exists, otherwise original name
 */
export async function mapPlayerName(name) {
  if (!name || typeof name !== 'string') {
    return name || '';
  }

  const trimmedName = name.trim();
  if (!trimmedName) {
    return '';
  }

  const mappings = await getMappings();
  const mapped = mappings.get(trimmedName.toLowerCase());

  return mapped || trimmedName;
}

/**
 * Map an array of player names
 * @param {string[]} names - Array of player names or usernames
 * @returns {Promise<string[]>} Array with mapped names
 */
export async function mapPlayerNames(names) {
  if (!Array.isArray(names)) {
    return [];
  }

  const mappings = await getMappings();

  return names.map(name => {
    if (!name || typeof name !== 'string') {
      return name || '';
    }
    const trimmedName = name.trim();
    const mapped = mappings.get(trimmedName.toLowerCase());
    return mapped || trimmedName;
  });
}

/**
 * Map player names in a formatted pair string (e.g., "username1 - username2")
 * @param {string} pairString - Formatted pair string
 * @param {string} separator - Separator between names (default: ' - ')
 * @returns {Promise<string>} Mapped pair string
 */
export async function mapPairString(pairString, separator = ' - ') {
  if (!pairString || typeof pairString !== 'string') {
    return pairString || '';
  }

  const names = pairString.split(separator).map(n => n.trim());
  const mapped = await mapPlayerNames(names);
  return mapped.join(separator);
}

/**
 * Add or update a player name mapping
 * @param {string} username - BBO username
 * @param {string} actualName - Actual player name
 * @param {string} notes - Optional notes
 * @returns {Promise<boolean>} Success status
 */
export async function addPlayerMapping(username, actualName, notes = '') {
  try {
    const { error } = await supabase
      .from('player_name_mappings')
      .upsert({
        username: username.trim(),
        actual_name: actualName.trim(),
        notes: notes.trim()
      }, {
        onConflict: 'username',
        ignoreDuplicates: false
      });

    if (error) {
      console.error('Error adding player mapping:', error);
      return false;
    }

    // Invalidate cache
    nameCache = null;
    cacheTimestamp = null;

    return true;
  } catch (error) {
    console.error('Exception adding player mapping:', error);
    return false;
  }
}

/**
 * Delete a player name mapping
 * @param {string} username - BBO username to remove
 * @returns {Promise<boolean>} Success status
 */
export async function deletePlayerMapping(username) {
  try {
    const { error } = await supabase
      .from('player_name_mappings')
      .delete()
      .ilike('username', username.trim());

    if (error) {
      console.error('Error deleting player mapping:', error);
      return false;
    }

    // Invalidate cache
    nameCache = null;
    cacheTimestamp = null;

    return true;
  } catch (error) {
    console.error('Exception deleting player mapping:', error);
    return false;
  }
}

/**
 * Get all player mappings for admin UI
 * @returns {Promise<Array>} Array of mapping objects
 */
export async function getAllMappings() {
  try {
    const { data, error } = await supabase
      .from('player_name_mappings')
      .select('*')
      .order('username');

    if (error) {
      console.error('Error fetching all mappings:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Exception fetching all mappings:', error);
    return [];
  }
}

/**
 * Clear the in-memory cache (useful for testing)
 */
export function clearCache() {
  nameCache = null;
  cacheTimestamp = null;
}

/**
 * Preload mappings into cache (call on app initialization)
 * @returns {Promise<void>}
 */
export async function preloadMappings() {
  await loadPlayerMappings();
}
